/**
  * @class meleKeyValues
  * @description
  *
  * # Purpose
  * Implements `<mele-keyvalues>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-keyvalues></mele-keyvalues>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-keyvalues',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  header: { type: 'string', description: 'primary header' },
  localize: { type: 'boolean', description: 'default localization of values' },
  items: { type: 'array', description: 'child items', noattribute: true },
}, meleElement._properties)

export default class meleKeyValues extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()

    const fragment = this.ownerDocument.createDocumentFragment()
    this._wrapper = this.createElementFast('mele-wrapper')
    fragment.appendChild(this._wrapper)

    this._header_wrapper = this.createElementFast('mele-header-wrapper')
    this._wrapper.appendChild(this._header_wrapper)
    this._header = this.createElementFast('mele-header')
    this._header_wrapper.appendChild(this._header)
    this._header.classList.add('empty')

    this._body_wrapper = this.createElementFast('mele-body-wrapper')
    this._wrapper.appendChild(this._body_wrapper)
    this._body = this.createElementFast('mele-body')
    this._body_wrapper.appendChild(this._body)

    this.appendChild(fragment)
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * when language change, refresh completely
  */
  languageChanged() {
    this.headerChanged()
    this.itemsChanged(this.items)
    super.languageChanged()
  } // languageChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * when localize change, refresh completely
  */
  localizeChanged() {
    this.headerChanged()
    this.itemsChanged(this.items)
    super.languageChanged()
  } // localizeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header"
  */
  headerChanged() {
    const html = this.translate(this.header, false)
    const el = this._header
    if(el.innerHTML !== html) el.innerHTML = html
    this._header.classList.toggle('empty', html.length===0)
  } // headerChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "header"
  */
  itemsChanged(items) {
    let data = []
    if(typeof items === 'string') {
      try{
        items = JSON.parse(items)
      }catch(e) {
        console.error('MELE-KEYVALUES: invalid items')
      }
    }

    if(!items) {
      data = []
    }else if(Array.isArray(items)) {
      for(const item of items) {
        if(Array.isArray(item)) {
          const [key, value, format, localize] = item
          data.push({ key, value, format, localize })
        }else if(item && typeof item === 'object') {
          const { key, value, format, localize } = item
          data.push({ key, value, format, localize })
        }else {
          // header item
          const value = String(item ?? '')
          data.push({ value, localize: true })
        }
      }
    }else if(typeof items === 'object') {
      for(const key in items) {
        const value = items[key]
        data.push({ key, value })
      }
    }

    const fragment = this.ownerDocument.createDocumentFragment()
    const localize_default = this.localize
    for(const item of data) {
      let { key, value, format, localize } = item
      key = String(key ?? '')
      value = String(value ?? '')
      format = String(format ?? '')
      localize = typeof localize === 'undefined' ? localize_default : !!localize
      if(localize) {
        value = this.translate(value, false)
      }
      const el = this.createElementFast('kv')
      if(key !== '') {
        el.setAttribute('key', key)
        const elk = this.createElementFast('k')
        elk.innerHTML = this.translate(key, false)
        el.appendChild(elk)
      }
      const elv = this.createElementFast('v')
      elv.innerHTML = value
      el.appendChild(elv)
      fragment.appendChild(el)
    }

    this.classList.toggle('empty', data.length===0)

    this._body.innerHTML = ''
    this._body.appendChild(fragment)
  } // itemsChanged
} // class meleKeyValues

meleKeyValues._properties = PROPERTIES
meleKeyValues.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-keyvalues', meleKeyValues)
